import { useQuery, useMutation, useQueryClient } from 'react-query';
import axiosClient from '@lib/utils/axiosClient';
import { makeResourceKeys } from 'requests/resources/makeResources/makeResourcesKeys';
import { snackbarError, snackbarSuccess } from 'actions/snackbarMessage';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from '../showIntegrationMessages';

export interface IVendorSecret {
  vendor: string;
  _id: string;
  key: string;
  secret?: string;
  createdAt: string;
  notes: { [key: string]: unknown };
}
interface GetProps {
  name: string;
}

interface EditProps {
  data: unknown;
  id: string;
}
interface DeleteProps {
  id: string;
}

interface CreateProps {
  data: { [key: string]: unknown; vendor: string };
}

interface IPostValidateVendorSecret {
  vendor: string;
  key: string;
  secret?: string;
}

const keys = makeResourceKeys('vendorsecrets');

// TODO create a vendorSecrets model in ui-models

// queryFns and MutateFns
async function queryFn() {
  const { data } = await axiosClient.get(`/vendorsecrets`);
  return data;
}

async function queryByNameFn(vendorName: string) {
  const { data } = await axiosClient.get(`/vendorsecrets?vendor=${vendorName}`);
  return data;
}

const createFn = ({ data }: CreateProps) => axiosClient.post(`/vendorsecrets`, data);

const editFn = ({ id, data }: { data: unknown; id: string }) => axiosClient.put(`/vendorsecrets/${id}`, data);

const deleteFn = ({ id }: { id: string }) => axiosClient.delete(`/vendorsecrets/${id}`);

const postValidate = ({ key, secret, vendor }: IPostValidateVendorSecret) =>
  axiosClient
    .post(`/vendorsecrets/validate/${vendor}`, {
      key,
      secret,
    })
    .then(({ data }) => data);
// Hooks

export const useGetAllVendorSecrets = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  return useQuery<{ response: IVendorSecret[] }>(keys.$all, () => queryFn(), {
    onError: () => dispatch(snackbarError(formatMessage(messages.fetchError))),
  });
};

export const useGetVendorSecretByName = ({ name }: GetProps) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  return useQuery<{ response: IVendorSecret[] }>(keys.$entityId(name), () => queryByNameFn(name), {
    onError: () => dispatch(snackbarError(formatMessage(messages.fetchError))),
  });
};

export const useCreateVendorSecret = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  return useMutation(({ data }: CreateProps) => createFn({ data }), {
    onSuccess: () => {
      queryClient.invalidateQueries(keys.$all);
      dispatch(snackbarSuccess(formatMessage(messages.saveSuccess)));
    },
    onError: () => {
      dispatch(snackbarError(formatMessage(messages.fetchError)));
    },
  });
};

export const useEditVendorSecret = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  return useMutation(({ data, id }: EditProps) => editFn({ data, id }), {
    onSuccess: () => {
      queryClient.invalidateQueries(keys.$all);
      dispatch(snackbarSuccess(formatMessage(messages.saveSuccess)));
    },
    onError: () => {
      dispatch(snackbarError(formatMessage(messages.fetchError)));
    },
  });
};

export const useDeleteVendorSecret = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  return useMutation(({ id }: DeleteProps) => deleteFn({ id }), {
    onSuccess: () => {
      queryClient.invalidateQueries(keys.$all);
      dispatch(snackbarSuccess(formatMessage(messages.saveSuccess)));
    },
    onError: () => {
      dispatch(snackbarError(formatMessage(messages.fetchError)));
    },
  });
};

export const usePostValidateVendorSecret = () => {
  const queryClient = useQueryClient();
  return useMutation(({ key, secret, vendor }: IPostValidateVendorSecret) => postValidate({ key, secret, vendor }), {
    onSuccess: () => queryClient.invalidateQueries(keys.$all),
  });
};
